export type StyleProperty = string | string[];
export type SystemValueType = 'color' | 'spacing' | 'size' | 'shadow' | 'identity';

export interface SystemPropData {
    type: SystemValueType;
    property: StyleProperty;
    transform?: (value: string | number) => string | number;
}

export type SystemPropKeys =
    | 'm'
    | 'mt'
    | 'mb'
    | 'ml'
    | 'mr'
    | 'mx'
    | 'my'
    | 'p'
    | 'py'
    | 'px'
    | 'pt'
    | 'pb'
    | 'pl'
    | 'pr'
    | 'w'
    | 'miw'
    | 'maw'
    | 'h'
    | 'mih'
    | 'mah'
    | 'bg'
    | 'c'
    | 'fill'
    | 'opacity'
    | 'pos'
    | 'top'
    | 'left'
    | 'bottom'
    | 'right'
    | 'inset'
    | 'display'
    | 'flex'
    | 'flexGrow'
    | 'flexShrink'
    | 'flexBasis'
    | 'alignContent'
    | 'alignItems'
    | 'alignSelf'
    | 'flexDirection'
    | 'justifyContent'
    | 'flexWrap'
    | 'order'
    | 'gap'
    | 'overflow'
    | 'overflowX'
    | 'overflowY'
    | 'zIndex'
    | 'ta'
    | 'tal'
    | 'va'
    | 'fontSize'
    | 'fw'
    | 'b'
    | 'bs'
    | 'bc'
    | 'bw'
    | 'btw'
    | 'bbw'
    | 'blw'
    | 'brw'
    | 'br'
    | 'btlr'
    | 'btrr'
    | 'bblr'
    | 'bbrr'
    | 'btr'
    | 'bbr'
    | 'transition'
    | 'transform'
    | 'backgroundImage'
    | 'backgroundSize'
    | 'backgroundRepeat'
    | 'backgroundPosition'
    | 'objectFit'
    | 'aspectRatio'
    | 'outline'
    | 'textShadow'
    | 'boxShadow'
    | 'pointerEvents'
    | 'filter';

export const SYSTEM_PROPS: Record<SystemPropKeys, SystemPropData> = {
    m: { type: 'spacing', property: 'margin' },
    mt: { type: 'spacing', property: 'marginBlockStart' },
    mb: { type: 'spacing', property: 'marginBlockEnd' },
    ml: { type: 'spacing', property: 'marginInlineStart' },
    mr: { type: 'spacing', property: 'marginInlineEnd' },
    mx: { type: 'spacing', property: 'marginInline' },
    my: { type: 'spacing', property: 'marginBlock' },

    p: { type: 'spacing', property: 'padding' },
    pt: { type: 'spacing', property: 'paddingBlockStart' },
    pb: { type: 'spacing', property: 'paddingBlockEnd' },
    pl: { type: 'spacing', property: 'paddingInlineStart' },
    pr: { type: 'spacing', property: 'paddingInlineEnd' },
    px: { type: 'spacing', property: 'paddingInline' },
    py: { type: 'spacing', property: 'paddingBlock' },

    w: { type: 'size', property: 'width' },
    miw: { type: 'size', property: 'minWidth' },
    maw: { type: 'size', property: 'maxWidth' },
    h: { type: 'size', property: 'height' },
    mih: { type: 'size', property: 'minHeight' },
    mah: { type: 'size', property: 'maxHeight' },

    bg: { type: 'color', property: 'background' },
    c: { type: 'color', property: 'color' },
    fill: { type: 'color', property: 'fill' },
    opacity: { type: 'identity', property: 'opacity' },

    pos: { type: 'identity', property: 'position' },
    top: { type: 'spacing', property: 'insetBlockStart' },
    left: { type: 'spacing', property: 'insetInlineStart' },
    bottom: { type: 'spacing', property: 'insetBlockEnd' },
    right: { type: 'spacing', property: 'insetInlineEnd' },
    inset: { type: 'spacing', property: 'inset' },

    display: { type: 'identity', property: 'display' },

    flex: { type: 'identity', property: 'flex' },
    flexGrow: { type: 'identity', property: 'flexGrow' },
    flexShrink: { type: 'identity', property: 'flexShrink' },
    flexBasis: { type: 'identity', property: 'flexBasis' },
    alignContent: { type: 'identity', property: 'alignContent' },
    alignItems: { type: 'identity', property: 'alignItems' },
    alignSelf: { type: 'identity', property: 'alignSelf' },
    flexDirection: { type: 'identity', property: 'flexDirection' },
    justifyContent: { type: 'identity', property: 'justifyContent' },
    flexWrap: { type: 'identity', property: 'flexWrap' },
    order: { type: 'identity', property: 'order' },
    gap: { type: 'spacing', property: 'gap' },

    overflow: { type: 'identity', property: 'overflow' },
    overflowX: { type: 'identity', property: 'overflowX' },
    overflowY: { type: 'identity', property: 'overflowY' },

    zIndex: { type: 'identity', property: 'zIndex' },

    ta: {
        type: 'identity',
        property: 'textAlign',
        transform: (value) => {
            if (value === 'left') {
                return 'start';
            }
            if (value === 'right') {
                return 'end';
            }
            return value;
        },
    },
    tal: {
        type: 'identity',
        property: 'textAlignLast',
        transform: (value) => {
            if (value === 'left') {
                return 'start';
            }
            if (value === 'right') {
                return 'end';
            }
            return value;
        },
    },
    va: { type: 'identity', property: 'verticalAlign' },

    fontSize: { type: 'size', property: 'fontSize' },
    fw: { type: 'identity', property: 'fontWeight' },

    b: { type: 'identity', property: 'border' },
    bs: { type: 'identity', property: 'borderStyle' },
    bc: { type: 'color', property: 'borderColor' },
    bw: { type: 'size', property: 'borderWidth' },
    btw: { type: 'size', property: 'borderBlockStartWidth' },
    bbw: { type: 'size', property: 'borderBlockEndWidth' },
    blw: { type: 'size', property: 'borderInlineStartWidth' },
    brw: { type: 'size', property: 'borderInlineEndWidth' },

    br: { type: 'size', property: 'borderRadius' },
    btr: { type: 'size', property: ['borderStartStartRadius', 'borderStartEndRadius'] },
    bbr: { type: 'size', property: ['borderEndStartRadius', 'borderEndEndRadius'] },
    btlr: { type: 'size', property: 'borderStartStartRadius' },
    btrr: { type: 'size', property: 'borderStartEndRadius' },
    bblr: { type: 'size', property: 'borderEndStartRadius' },
    bbrr: { type: 'size', property: 'borderEndEndRadius' },

    transition: { type: 'identity', property: 'transition' },
    transform: { type: 'identity', property: 'transform' },

    backgroundImage: { type: 'identity', property: 'backgroundImage' },
    backgroundSize: { type: 'identity', property: 'backgroundSize' },
    backgroundRepeat: { type: 'identity', property: 'backgroundRepeat' },
    backgroundPosition: { type: 'identity', property: 'backgroundPosition' },

    objectFit: { type: 'identity', property: 'objectFit' },
    aspectRatio: { type: 'identity', property: 'aspectRatio' },
    outline: { type: 'identity', property: 'outline' },
    textShadow: { type: 'identity', property: 'textShadow' },

    boxShadow: { type: 'shadow', property: 'boxShadow' },

    pointerEvents: { type: 'identity', property: 'pointerEvents' },

    filter: { type: 'identity', property: 'filter' },
};
